import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useState, useRef, useEffect } from 'react';
import Collapse from '@mui/material/Collapse';
import Autocomplete from '@Components/MuiWrappers/Autocomplete';
import AnimatedQuickFindLabel from '@Components/Header/AnimatedQuickFindLabel';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import { useSpeechRecognition, ListeningIndicator } from '@Components/Utilities/SpeechRecognition';
import { gtmEventType } from '@Components/Utilities/SandhillsConstants';
import { gtmPushEvent } from '@Components/Utilities/SandhillsUtility';

const QuickFind = (props) => {
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const [keywordSearchState, setKeywordSearchState] = useRecoilState(atoms.keywordSearchStateAtom);
    const [focused, setFocused] = useState(false);
    const [searchType, setSearchType] = useState('Equipment');
    const [backdropMargin, setBackdropMargin] = useState(0);
    const resizeObserverRef = useRef(null);
    const scrollPosition = useRef(0);
    const isMobile = useRef(false);
    const isClient = useRef(typeof window !== 'undefined');
    const quickFindWrapperRef = useRef(null);
    const windowSize = useWindowSize();
    const {
        transcript,
        isListening,
        startListening,
        stopListening,
        setTranscript,
        isSpeechRecognitionSupported
    } = useSpeechRecognition();

    useEffect(() => {
        if (!isClient.current) return;
        // Detect mobile devices
        isMobile.current = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }, []);

    useEffect(() => {
        if (!isClient.current) return;

        if (navigator && "virtualKeyboard" in navigator) {
            navigator.virtualKeyboard.overlaysContent = true;
        }

        // Initialize measurements and observers
        setupResizeObserver(); 

        if (focused) {
            // Calculate backdrop margin
            const margin = getBackdropMargin();
            setBackdropMargin(margin);

            scrollPosition.current = window.scrollY; // Store current scroll position

            // For all devices, just prevent background scrolling without position:fixed
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = ''; // Reset styles
        }

        return () => {
            if (!isClient.current) return;

            // Disconnect the ResizeObserver
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }
            // Always reset styles on unmount
            document.body.style.overflow = '';
        };
    }, [focused]);

    useEffect(() => {
        if (transcript?.length > 0) {
            setKeywordSearchState(prevState => ({
                ...prevState,
                SearchText: transcript
            }));
        }
    }, [transcript]);

    const onListenStart = (e) => {
        e.stopPropagation();
        setTranscript('');
        startListening();
    };

    const onListenStop = (e) => {
        // stop listening and ensure input has focus and options are shown for the transcript value
        stopListening();
        setFocused(true);
    };

    const closeBackdrop = () => {
        setFocused(false);
    };

    const onTextChange = (e) => {
        if (!e || !e.target || e.key === 'Enter') return;

        setKeywordSearchState(prevState => ({
            ...prevState,
            SearchText: e.target.value, 
        }));
    }

    const setupResizeObserver = () => {
        const container = document.querySelector(props.container);

        // Create a new ResizeObserver
        resizeObserverRef.current = new ResizeObserver(entries => {
            // Update the backdrop margin when observed elements resize
            const margin = getBackdropMargin();
            setBackdropMargin(margin);
        });

        if (container) {
            resizeObserverRef.current.observe(container);
        }
    };

    const getBackdropMargin = () => {
        const container = document.querySelector(props.container);
        if (container) {
            const rect = container.getBoundingClientRect();
            return rect.bottom;
        }
        return 0;
    };

    const preventDefault = (e) => {
        // Function to prevent scroll on backdrop & prevent losing focus on input when clicking search tabs
        e.preventDefault();
    };

    // Simple iOS-specific backdrop height calculation
    const getBackdropHeight = () => {
        // On iOS, use window.innerHeight instead of 100vh to handle permission dialogs
        if (isMobile.current && /iPad|iPhone|iPod/.test(navigator.userAgent)) {
            return `${window.innerHeight - backdropMargin}px`;
        }
        return `calc(100vh - ${backdropMargin}px)`;
    };

    const setSearchTypeTabFocus = (e, key) => {
        e.preventDefault();
        setSearchType(key);
        setFocused(true);
    };

    useEffect(() => {
        if (isListening && !focused) {
            setFocused(true);
        }
    }, [isListening])

    return (
        <>
            <div
                className="quick-find quick-find-wrapper"
                ref={quickFindWrapperRef}
                onFocus={(e) => setFocused(true)}
                onBlur={(e) => setFocused(false)}
                style={{
                    boxShadow: focused ? 'unset' : '0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 10%), 0px 1px 5px 0px rgb(0 0 0 / 10%)',
                    borderRadius: focused ? '5px' : '50px'
                }}
            >
                {focused && (
                    <div
                        className="quick-find-backdrop"
                        onClick={closeBackdrop}
                        onTouchMove={preventDefault}
                        style={{
                            position: 'fixed',
                            top: `${backdropMargin}px`,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            height: getBackdropHeight(),
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 99999,
                            transition: 'opacity 0.5s ease',
                            touchAction: 'none',
                            willChange: 'opacity'
                        }}
                    />
                )}

                {keywordSearchState?.QuickFindSearchTypes && Object.keys(keywordSearchState.QuickFindSearchTypes).length > 1 &&
                    <Collapse
                        in={focused}
                        timeout={500}
                    >
                        <div className='search-selector'>
                            <ul>
                                {Object.entries(keywordSearchState.QuickFindSearchTypes).map(([key, value], index) => {
                                    return (
                                        <li key={`search-tab-${index}`} onClick={(e) => { setSearchTypeTabFocus(e, key); gtmPushEvent(gtmEventType.keywordTabClick); }} onMouseDown={preventDefault}>
                                            <button className={'search-type-button' + (searchType === key ? ' active' : '')}>
                                                {value}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </Collapse>
                }

                <Autocomplete
                    name='QuickFindCode'
                    id={props.isStickyHeader ? 'animated-quick-find-sticky' : 'animated-quick-find'}
                    label={<AnimatedQuickFindLabel {...props} focused={focused} defaultText={props.defaultText} />}
                    defaultLabel={props.defaultText ? props.defaultText : keywordSearchState.QuickFindPlaceholder}
                    styles={{ bgVariant: currentSite?.Site?.IsRVUniverse ? 'white' : null }}
                    showHistory
                    openOnFocus
                    searchType={searchType}
                    partsUrl={keywordSearchState.PartsSearchURL}
                    attachmentsUrl={keywordSearchState.AttachmentsSearchURL}
                    isFocused={focused}
                    value={keywordSearchState.SearchText}
                    hideLabel={true}
                    setIsFocused={setFocused}
                    disableUnderline={true}
                    autocompleteSearchField={true}
                    isStickyHeader={props.isStickyHeader}
                    enableVoiceToText={
                        windowSize.isMobile &&
                        isSpeechRecognitionSupported
                    }
                    onListenStart={onListenStart}
                    onListenStop={onListenStop}
                    isListening={isListening}
                    InputProps={{
                        startAdornment: isListening ? <ListeningIndicator /> : null,
                        endAdornment: <div style={{ position: 'absolute', right: '0px', height: '100%' }}>
                            {windowSize.isMobile && isSpeechRecognitionSupported ?
                                <button className="voice-btn" onClick={isListening ? onListenStop : onListenStart} aria-label={isListening ? "Stop Voice To Text" : "Start Voice To Text"}>
                                    <i className={isListening ? "fa-solid fa-square" : "fa-solid fa-microphone"}></i>
                                </button> : null}
                        </div>
                    }}
                    disableClearable={true}
                    onTextChange={onTextChange}
                    isQuickFind={true}
                    SearchURLs={props.SearchURLs}
                />
            </div>
        </>
    );
};

export default QuickFind;